import React, { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import {
  ActionButton,
  FormDialog,
  IMAGE_FILE_ACCEPT,
  RhfFileInput,
} from '@core/components';
import { useProgressiveImageSrc } from '@core/hooks/useProgressiveImageSrc';
import type { Design } from '../../api/design';
import type { DesignPreset } from '../../api/designPreset';
import type { VestmentType } from '../enum/vestmentType';
import {
  type DesignFormValues,
  designToFormValues,
  submitDesign,
} from '../utils/design';
import { getPresetFeatures } from '../utils/designPreset';
import { getImageFileUrl, getImageThumbnailUrl } from '../utils/image';
import { DESIGN_VESTMENT_TYPE_OPTIONS } from '../utils/vestment';
import { DesignFormFields } from './DesignFormFields';
import DesignPresetDialog from './DesignPresetDialog';

interface DesignDialogProps {
  onClose: () => void;
  design?: Design | null;
  onSuccess?: (design: Design, mode: 'create' | 'update') => void;
  /** Prefills the code of a new design. */
  inputValue?: string;
  /**
   * Limits the vestment type options. A single option is preselected and the
   * field disabled.
   */
  vestmentTypes?: VestmentType[];
  onPresetAdded?: (preset: DesignPreset) => void;
}

const DesignDialog: React.FC<DesignDialogProps> = ({
  onClose,
  design,
  onSuccess,
  inputValue,
  vestmentTypes,
  onPresetAdded,
}) => {
  const vestmentTypeOptions = useMemo(
    () =>
      vestmentTypes
        ? DESIGN_VESTMENT_TYPE_OPTIONS.filter((option) =>
            vestmentTypes.includes(option.value as VestmentType)
          )
        : DESIGN_VESTMENT_TYPE_OPTIONS,
    [vestmentTypes]
  );

  const onlyVestmentType =
    vestmentTypes?.length === 1 ? vestmentTypes[0] : undefined;

  const defaultValues = useMemo(
    () => designToFormValues(design, inputValue, onlyVestmentType),
    [design, inputValue, onlyVestmentType]
  );

  const imagePreviewSrc = useProgressiveImageSrc(
    getImageThumbnailUrl(design),
    getImageFileUrl(design)
  );

  const [presetDesign, setPresetDesign] = useState<Design | null>(null);
  const presetFeatures = getPresetFeatures(presetDesign?.vestmentType);

  const onSubmit = async (
    data: DesignFormValues,
    methods: UseFormReturn<DesignFormValues>
  ) => {
    const saved = await submitDesign(data, design, methods);
    onSuccess?.(saved, design ? 'update' : 'create');
  };

  const addPreset = (
    current: Design,
    methods: UseFormReturn<DesignFormValues>
  ) => {
    if (!methods.formState.isDirty) {
      setPresetDesign(current);
      return;
    }
    void methods.handleSubmit(async (data) => {
      const saved = await submitDesign(data, current, methods).catch(
        () => null
      );
      if (!saved) {
        return;
      }
      methods.reset(designToFormValues(saved));
      onSuccess?.(saved, 'update');
      setPresetDesign(saved);
    })();
  };

  return (
    <>
      <FormDialog<DesignFormValues>
        open
        onClose={onClose}
        title={design ? 'Edit Design' : 'Add Design'}
        titleAction={(methods) =>
          design && getPresetFeatures(methods.watch('vestmentType') || null) ? (
            <ActionButton
              variant="outline"
              size="sm"
              icon={<AddIcon />}
              loading={methods.formState.isSubmitting}
              onClick={() => addPreset(design, methods)}
            >
              Add preset
            </ActionButton>
          ) : null
        }
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        submitText={design ? 'Update' : 'Add'}
        cancelText="Cancel"
      >
        <DesignFormFields vestmentTypeOptions={vestmentTypeOptions} />
        <RhfFileInput<DesignFormValues>
          name="image"
          label="Image"
          accept={IMAGE_FILE_ACCEPT}
          initialPreviewSrc={imagePreviewSrc}
        />
      </FormDialog>
      {presetDesign && presetFeatures ? (
        <DesignPresetDialog
          open
          onClose={() => setPresetDesign(null)}
          design={presetDesign}
          features={presetFeatures}
          onSuccess={onPresetAdded}
        />
      ) : null}
    </>
  );
};

export default DesignDialog;
